import { lazy, Suspense, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Button, Dropdown, type MenuProps } from 'antd';
import { AppstoreOutlined, LinkOutlined, LogoutOutlined, ReloadOutlined } from '@ant-design/icons';

import { APP_ROUTES, MANAGER_ROUTES } from '../constants';
import { buildSorareConnectUrl } from '../constants/api';
import { AUTH_TEXT } from '../constants/text';
import { TEST_IDS } from '../constants/testIds';
import { useLogout, useSession } from '../queries/useSession';
import styles from './ConnectSorareButton.module.css';

const RefreshCardsModal = lazy(() => import('./RefreshCardsModal'));

const MENU_KEYS = {
  MY_CARDS: 'my-cards',
  REFRESH_CARDS: 'refresh-cards',
  DISCONNECT: 'disconnect',
} as const;

/**
 * Header control for the Sorare connection: a "Connect to Sorare" button when disconnected,
 * the manager's avatar + dropdown when connected.
 *
 * The two states are keyed off *different* fields on purpose. `connectEnabled` is the rollback
 * switch (the API unsets its OAuth credentials and stops issuing new connections); existing
 * sessions keep resolving. Keying the connected UI off it would log every connected user out of
 * the UI the moment the feature is rolled back — the exact failure the API contract is designed
 * to avoid. So: `manager` non-null → connected UI, always. `connectEnabled` only gates the
 * Connect button.
 */
const ConnectSorareButton = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data } = useSession();
  const { mutateAsync: logout } = useLogout();

  const [isConnecting, setConnecting] = useState(false);
  const [isRefreshCardsModalVisible, setRefreshCardsModalVisible] = useState(false);

  const manager = data?.manager ?? null;
  const connectEnabled = data?.connectEnabled ?? false;

  // NOTE: handling of the `?connect=ok|denied|error` marker deliberately does NOT live here.
  // On mobile this component renders inside the nav Drawer's `extra`, which antd leaves
  // unmounted until the drawer is opened — so the notification would never fire and the param
  // would stay stuck in the URL. It lives in `useConnectOutcome`, hosted by `AppLayout`.

  // Connecting is a full-page browser navigation to the API (which 302s to Sorare and back),
  // not an axios call — so the origin-qualified URL from buildSorareConnectUrl, and
  // `location.assign` rather than the router.
  const handleConnect = () => {
    setConnecting(true);
    window.location.assign(buildSorareConnectUrl(`${location.pathname}${location.search}`));
  };

  const menuItems: MenuProps['items'] = [
    { key: MENU_KEYS.MY_CARDS, label: AUTH_TEXT.MY_CARDS, icon: <AppstoreOutlined /> },
    { key: MENU_KEYS.REFRESH_CARDS, label: AUTH_TEXT.REFRESH_MY_CARDS, icon: <ReloadOutlined /> },
    { key: MENU_KEYS.DISCONNECT, label: AUTH_TEXT.DISCONNECT, icon: <LogoutOutlined /> },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (!manager) {
      return;
    }
    if (key === MENU_KEYS.MY_CARDS) {
      navigate(`${APP_ROUTES.MANAGER}/${manager.slug}${MANAGER_ROUTES.CARDS}`);
    } else if (key === MENU_KEYS.REFRESH_CARDS) {
      setRefreshCardsModalVisible(true);
    } else if (key === MENU_KEYS.DISCONNECT) {
      // The hook owns the cache reset; a rejected logout leaves the session as-is.
      logout().catch(() => {});
    }
  };

  if (manager) {
    return (
      <>
        <Dropdown
          menu={{ items: menuItems, onClick: handleMenuClick }}
          trigger={['click']}
          placement="bottomRight"
        >
          <button
            type="button"
            className={styles.trigger}
            aria-haspopup="menu"
            aria-label={manager.name}
            data-testid={TEST_IDS.SESSION_MENU}
          >
            <Avatar
              src={manager.pictureUrl ?? undefined}
              size={24}
              data-testid={TEST_IDS.SESSION_AVATAR}
            />
            <span className={styles.nickname}>{manager.name}</span>
          </button>
        </Dropdown>
        {isRefreshCardsModalVisible && (
          <Suspense fallback={null}>
            {/* Prefilled with the CONNECTED manager's slug — deliberately independent of the
                pathname slug the Menu's own "Refresh All Cards" entry uses, so browsing another
                manager's page never refreshes the wrong account. */}
            <RefreshCardsModal
              isVisible={isRefreshCardsModalVisible}
              setVisible={setRefreshCardsModalVisible}
              managerSlug={manager.slug}
            />
          </Suspense>
        )}
      </>
    );
  }

  // Not connected: render the Connect button only when the API says new connections are
  // possible. (The `?connect=` outcome notice is handled by AppLayout, not here.)
  return (
    <>
      {connectEnabled && (
        <Button
          type="primary"
          size="small"
          className={styles.connectButton}
          icon={<LinkOutlined />}
          loading={isConnecting}
          onClick={handleConnect}
          data-testid={TEST_IDS.CONNECT_SORARE}
        >
          {isConnecting ? AUTH_TEXT.CONNECTING : AUTH_TEXT.CONNECT}
        </Button>
      )}
    </>
  );
};

export default ConnectSorareButton;
